import React, { useEffect, useRef } from 'react';
import { generatePlacementInterpretation } from '../../lib/placementInterpretation';
import { generateAspectInterpretation } from '../../lib/aspectInterpretation';
import { formatPlacementLabel } from '../../lib/chartFormat';

const HOUSE_MEANINGS = {
  1: 'Identity and first approach',
  2: 'Resources and self-worth',
  3: 'Learning and communication',
  4: 'Roots and foundations',
  5: 'Creativity and expression',
  6: 'Routines and craft',
  7: 'Relationships and mirrors',
  8: 'Depth and shared bonds',
  9: 'Belief and perspective',
  10: 'Direction and contribution',
  11: 'Community and future',
  12: 'Retreat and integration',
};

const ANGLE_CONTEXT = {
  ASC: 'ASC anchors first approach, embodiment, and how new cycles begin.',
  MC: 'MC marks directional growth, public contribution, and the vocation axis.',
  DSC: 'DSC describes partnership dynamics and what relationships mirror back.',
  IC: 'IC points to roots, belonging, and emotional foundation patterns.',
};

const SelectionContextPanel = ({ chart, selectedPlanetKey, selectedHouse, selectedAspectId, selectedAngleKey }) => {
  const panelRef = useRef(null);
  const selectionKey = `${selectedPlanetKey}-${selectedHouse}-${selectedAspectId}-${selectedAngleKey}`;

  useEffect(() => {
    if (!panelRef.current || !selectionKey) return;
    panelRef.current.classList.remove('chart-context-panel-highlight');
    void panelRef.current.offsetWidth;
    panelRef.current.classList.add('chart-context-panel-highlight');
    const t = setTimeout(() => {
      panelRef.current?.classList.remove('chart-context-panel-highlight');
    }, 1200);
    return () => clearTimeout(t);
  }, [selectionKey]);

  if (!chart) return null;
  const placements = chart.planets || [];
  const houses = chart.houses || [];
  const aspects = chart.aspects || [];

  if (selectedAngleKey) {
    const label = String(selectedAngleKey).toUpperCase();
    const angle = chart.angles?.[label.toLowerCase()] || null;
    return (
      <section ref={panelRef} className="card chart-context-panel chart-context-panel-secondary">
        <h3>{label} — Chart angle focus</h3>
        <p>
          {ANGLE_CONTEXT[label] || 'This angle sets a key structural axis in chart interpretation.'}
        </p>
        {angle && (
          <p>
            Current placement: {angle.sign} {angle.degreeText}.
          </p>
        )}
      </section>
    );
  }

  if (selectedAspectId) {
    const aspect = aspects.find((item) => item.id === selectedAspectId);
    if (aspect) {
      const p1 = placements.find((p) => p.key === (aspect.p1 || '').toLowerCase());
      const p2 = placements.find((p) => p.key === (aspect.p2 || '').toLowerCase());
      const aspectWithLabels = {
        ...aspect,
        p1Label: aspect.p1Label || formatPlanetName(aspect.p1),
        p2Label: aspect.p2Label || formatPlanetName(aspect.p2),
      };
      const interp = generateAspectInterpretation({ aspect: aspectWithLabels, p1, p2 });
      if (interp) {
        return (
          <section ref={panelRef} className="card chart-context-panel chart-context-panel-secondary">
            <h3>{interp.title}</h3>
            {interp.paragraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </section>
        );
      }
    }
  }

  if (selectedHouse) {
    const house = houses.find((item) => Number(item.house) === Number(selectedHouse));
    const planetsInHouse = placements.filter((item) => Number(item.house) === Number(selectedHouse));
    return (
      <section ref={panelRef} className="card chart-context-panel chart-context-panel-secondary">
        <h3>{ordinalHouse(selectedHouse)} House — {HOUSE_MEANINGS[selectedHouse] || 'Life arena focus'}</h3>
        <p>
          The {ordinalHouse(selectedHouse)} house describes {houseTheme(selectedHouse)}. With {house?.sign || 'this sign'} on the cusp,
          this arena tends to move through {house?.sign || 'its'} tone.
        </p>
        {planetsInHouse.length > 0 && (
          <p>
            Active here now: {planetsInHouse.map((item) => `${item.name} in ${item.sign}`).join(', ')}.
          </p>
        )}
      </section>
    );
  }

  const placement = placements.find((item) => item.key === selectedPlanetKey) || placements[0];
  if (!placement) return null;
  const interpretation = generatePlacementInterpretation({ placement });
  return (
    <section ref={panelRef} className="card chart-context-panel chart-context-panel-secondary">
      <h3>{formatPlacementLabel(placement)}</h3>
      <p>{interpretation.corePattern}</p>
      <p>{interpretation.observation}</p>
    </section>
  );
};

function capitalize(value) {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function formatPlanetName(key) {
  const map = {
    sun: 'Sun',
    moon: 'Moon',
    mercury: 'Mercury',
    venus: 'Venus',
    mars: 'Mars',
    jupiter: 'Jupiter',
    saturn: 'Saturn',
    uranus: 'Uranus',
    neptune: 'Neptune',
    pluto: 'Pluto',
    northnode: 'North Node',
    lilith: 'Lilith',
    chiron: 'Chiron',
  };
  return map[key] || key;
}

function houseTheme(house) {
  const map = {
    1: 'identity, body, and first response',
    2: 'resources, value, and security',
    3: 'language, learning, and local patterning',
    4: 'home, roots, and emotional base',
    5: 'creativity, pleasure, and self-expression',
    6: 'work rhythms, service, and health routines',
    7: 'partnerships, contracts, and direct mirrors',
    8: 'shared trust, intimacy, and transformation',
    9: 'belief, study, and worldview',
    10: 'direction, public role, and vocation',
    11: 'community, friendships, and future aims',
    12: 'retreat, integration, and inner processing',
  };
  return map[Number(house)] || 'the key life themes for this sector';
}

export default SelectionContextPanel;
